import { z } from 'zod';
import { rutSchema, emailSchema, phoneSchema } from '@/schemas/shared';

// Tab 1: Información Empresa Schema
const createCompanyInfoBaseSchema = z.object({
  rutComplete: rutSchema,

  companyName: z
    .string()
    .min(1, 'La razón social es requerida')
    .max(100, 'La razón social no puede exceder 100 caracteres'),

  companyNameFantasy: z
    .union([z.string().max(1000, 'El nombre de fantasía no puede exceder 1000 caracteres'), z.null()])
    .optional()
    .default(null)
    .transform((value) => value ?? ''),

  companyAddress: z
    .string()
    .min(1, 'La dirección es requerida')
    .max(100, 'La dirección no puede exceder 100 caracteres'),

  companyAddressNumber: z
    .string()
    .min(1, 'El número de dirección es requerido')
    .max(50, 'El número de dirección no puede exceder 50 caracteres'),

  regionId: z
    .union([
      z.coerce.number({
        invalid_type_error: 'La región debe ser un valor válido',
      })
        .int('La región debe ser un número entero')
        .positive('La región debe ser un valor válido'),
      z.null(),
    ]),

  communeId: z
    .union([
      z.coerce.number({
        invalid_type_error: 'La comuna debe ser un valor válido',
      })
        .int('La comuna debe ser un número entero')
        .positive('La comuna debe ser un valor válido'),
      z.null(),
    ]),

  namePersonContact: z
    .string()
    .min(1, 'El nombre de contacto es requerido')
    .max(500, 'El nombre de contacto no puede exceder 500 caracteres'),

  emailPersonContact: emailSchema,

  telephonePersonContact: phoneSchema,
});

// Tab 1: Información Empresa Schema with required validations
export const createCompanyInfoSchema = createCompanyInfoBaseSchema
  .refine(
    (data) => data.regionId !== null,
    {
      message: 'La región es requerida',
      path: ['regionId'],
    },
  )
  .refine(
    (data) => data.communeId !== null,
    {
      message: 'La comuna es requerida',
      path: ['communeId'],
    },
  );

// Tab 2: Configuración Schema (base object without refinements)
const createCompanyConfigBaseSchema = z.object({
  fatherCompanyId: z
    .coerce
    .number({
      required_error: 'La empresa padre es requerida',
      invalid_type_error: 'La empresa padre debe ser un valor válido',
    })
    .int('La empresa padre debe ser un número entero')
    .positive('La empresa padre debe ser un valor válido'),

  terminateEmployeesWithExpiredContract: z
    .boolean()
    .default(false),

  estCompanyType: z
    .union([
      z.literal('T'),
      z.null(),
    ])
    .optional()
    .default(null),

  synchronizeWithREX: z
    .boolean()
    .default(false),

  rexid: z
    .union([z.string().max(50, 'El ID REX no puede exceder 50 caracteres'), z.null()])
    .optional()
    .default(null)
    .transform((value) => value ?? ''),

  rexurl: z
    .union([z.string().max(50, 'La URL REX no puede exceder 50 caracteres'), z.null()])
    .optional()
    .default(null)
    .transform((value) => value ?? ''),

});

// Tab 2: Configuración Schema with refinements
export const createCompanyConfigSchema = createCompanyConfigBaseSchema
  .refine(
    (data) => {
      // Si synchronizeWithREX es true, rexid y rexurl son requeridos
      if (data.synchronizeWithREX) {
        return !!data.rexid && data.rexid.trim().length > 0;
      }
      return true;
    },
    {
      message: 'El ID REX es requerido cuando se activa la sincronización con REX',
      path: ['rexid'],
    },
  )
  .refine(
    (data) => {
      if (data.synchronizeWithREX) {
        return !!data.rexurl && data.rexurl.trim().length > 0;
      }
      return true;
    },
    {
      message: 'La URL REX es requerida cuando se activa la sincronización con REX',
      path: ['rexurl'],
    },
  );

// Combined schema for full form validation
export const createCompanyFormSchema = z.object({
  ...createCompanyInfoBaseSchema.shape,
  ...createCompanyConfigBaseSchema.shape,
})
  .refine(
    (data) => data.regionId !== null,
    {
      message: 'La región es requerida',
      path: ['regionId'],
    },
  )
  .refine(
    (data) => data.communeId !== null,
    {
      message: 'La comuna es requerida',
      path: ['communeId'],
    },
  )
  .refine(
    (data) => {
      if (data.synchronizeWithREX) {
        return !!data.rexid && data.rexid.trim().length > 0;
      }
      return true;
    },
    {
      message: 'El ID REX es requerido cuando se activa la sincronización con REX',
      path: ['rexid'],
    },
  )
  .refine(
    (data) => {
      if (data.synchronizeWithREX) {
        return !!data.rexurl && data.rexurl.trim().length > 0;
      }
      return true;
    },
    {
      message: 'La URL REX es requerida cuando se activa la sincronización con REX',
      path: ['rexurl'],
    },
  );

// Infer TypeScript types from schemas
export type CreateCompanyInfo = z.infer<typeof createCompanyInfoSchema>;
export type CreateCompanyConfig = z.infer<typeof createCompanyConfigSchema>;
export type CreateCompanyForm = z.infer<typeof createCompanyFormSchema>;
